<template>
  <div class="checkout-page">
    <div class="card checkout-card">
      <div v-if="state === 'loading'" class="state-block">
        <div class="loader" />
        <p>Gerando seu PIX...</p>
      </div>

      <div v-else-if="state === 'erro'" class="state-block">
        <div class="state-icon">⚠️</div>
        <p>{{ erroMsg }}</p>
        <button class="btn-secondary" @click="loadCheckout">Tentar novamente</button>
      </div>

      <template v-else>
        <header class="header">
          <div class="evento-nome">{{ inscricao.evento.nome }}</div>
          <h1>{{ cobrancaGerada ? 'Pague com PIX' : 'Próxima parcela' }}</h1>
          <p class="participante">{{ inscricao.participanteNome }}</p>
        </header>

        <div class="info-bar">
          <div>
            <span class="info-label">Plano</span>
            <strong>{{ inscricao.plano.nome }}</strong>
          </div>
          <div v-if="totalParcelas > 1" class="info-divider">
            <span class="info-label">Parcela</span>
            <strong>{{ parcelaAtual }} de {{ totalParcelas }}</strong>
          </div>
          <div class="info-divider valor">
            <span class="info-label">Valor</span>
            <strong>{{ formatMoney(pagamento.valor) }}</strong>
          </div>
        </div>

        <div v-if="pix" class="pix-section">
          <div class="qr-wrap">
            <img v-if="pix.qrCodeBase64" :src="`data:image/png;base64,${pix.qrCodeBase64}`" alt="QR Code PIX" class="qr-img" />
            <div v-else class="qr-placeholder">QR indisponível</div>
          </div>

          <div class="copia-cola-wrap">
            <p class="copia-help">Use o app do seu banco. Escaneie o QR Code ou copie o código abaixo.</p>
            <div class="copia-row">
              <code class="copia-code">{{ pix.copiaECola }}</code>
              <button class="btn-copy" @click="copiar">{{ copiado ? 'Copiado!' : 'Copiar' }}</button>
            </div>
            <p v-if="pix.expiraEm" class="expira">Expira em {{ formatDateTime(pix.expiraEm) }}</p>
          </div>
        </div>

        <div v-else-if="!cobrancaGerada" class="pix-aguardando">
          <p>Esta parcela ainda não tem cobrança gerada.</p>
          <p class="vence">Vence em {{ formatDate(pagamento.vencimento) }}. O PIX chega por e-mail antes do vencimento.</p>
        </div>

        <div v-else class="pix-erro">
          <p>QR PIX indisponível agora. Tente atualizar a página.</p>
        </div>

        <div v-if="cobrancaGerada" class="poll-bar">
          <div class="dot" :class="{ active: pollAtivo }"></div>
          <span>Aguardando seu pagamento...</span>
        </div>

        <div class="footer-instructions">
          <p><strong>Após pagar</strong>, esta página vai atualizar sozinha. Você não precisa fazer mais nada.</p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getCheckout, getCheckoutStatus } from '../../lib/inscricao'

export default {
  name: 'CheckoutPage',
  data() {
    return {
      state: 'loading',
      erroMsg: '',
      inscricao: null,
      pagamento: null,
      pix: null,
      parcelaAtual: 1,
      totalParcelas: 1,
      copiado: false,
      pollAtivo: true,
      pollHandle: null,
      timeoutHandle: null
    }
  },
  computed: {
    // Parcela futura existe como divida antes de existir cobranca: nao ha QR
    // pra mostrar, e mandar o inscrito "atualizar a pagina" so o faria recarregar
    // em loop atras de algo que ainda nao existe.
    cobrancaGerada() {
      return this.pagamento?.cobrancaGerada !== false
    }
  },
  async mounted() {
    await this.loadCheckout()
    if (this.cobrancaGerada) {
      this.startPolling()
      this.timeoutHandle = setTimeout(() => this.stopPolling('timeout'), 30 * 60 * 1000)
    }
  },
  beforeUnmount() {
    this.stopPolling()
  },
  methods: {
    async loadCheckout() {
      this.state = 'loading'
      try {
        const data = await getCheckout(this.$route.params.inscricaoId)
        this.inscricao = data.inscricao
        this.pagamento = data.pagamento
        this.pix = data.pix
        this.parcelaAtual = data.pagamento?.parcelaAtual || 1
        this.totalParcelas = data.pagamento?.totalParcelas || 1
        if (!this.pagamento) {
          this.$router.push(`/sucesso/${this.$route.params.inscricaoId}`)
          return
        }
        this.state = 'ready'
      } catch (err) {
        this.state = 'erro'
        this.erroMsg = err.response?.data?.message || 'Falha ao carregar checkout.'
      }
    },
    startPolling() {
      this.pollHandle = setInterval(async () => {
        try {
          const { statusPagamento } = await getCheckoutStatus(this.$route.params.inscricaoId)
          if (statusPagamento === 'QUITADA' || statusPagamento === 'PARCIAL') {
            this.stopPolling()
            this.$router.push(`/sucesso/${this.$route.params.inscricaoId}`)
          }
        } catch {}
      }, 5000)
    },
    stopPolling(reason) {
      if (this.pollHandle) { clearInterval(this.pollHandle); this.pollHandle = null }
      if (this.timeoutHandle) { clearTimeout(this.timeoutHandle); this.timeoutHandle = null }
      if (reason === 'timeout') this.pollAtivo = false
    },
    async copiar() {
      if (!this.pix?.copiaECola) return
      try {
        await navigator.clipboard.writeText(this.pix.copiaECola)
        this.copiado = true
        setTimeout(() => { this.copiado = false }, 2500)
      } catch {
        this.$toast?.open?.({ message: 'Falha ao copiar', type: 'error', position: 'top-right' })
      }
    },
    formatMoney(v) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)
    },
    formatDateTime(iso) {
      try { return new Date(iso).toLocaleString('pt-BR') } catch { return iso }
    },
    formatDate(iso) {
      try { return new Date(iso).toLocaleDateString('pt-BR') } catch { return iso }
    }
  }
}
</script>

<style scoped>
.checkout-page {
  min-height: calc(100vh - 60px);
  display: flex; justify-content: center; align-items: flex-start;
  padding: var(--spacing-lg) var(--spacing-base);
}
.checkout-card {
  width: 100%; max-width: 620px;
  padding: var(--spacing-xl);
}
.state-block {
  display: flex; flex-direction: column; align-items: center; gap: var(--spacing-md);
  padding: var(--spacing-xl) 0; text-align: center;
}
.state-icon { font-size: 56px; }
.header { text-align: center; margin-bottom: var(--spacing-lg); }
.evento-nome { font-size: var(--text-sm); color: var(--color-accent); text-transform: uppercase; letter-spacing: 1px; }
.header h1 { font-size: var(--text-2xl); margin: 4px 0; }
.participante { color: var(--color-text-muted); font-size: var(--text-sm); }
.info-bar {
  display: grid; grid-template-columns: 1fr 1fr 1fr; gap: var(--spacing-base);
  padding: var(--spacing-base);
  background: var(--color-bg-hover);
  border-radius: var(--radius-md);
  margin-bottom: var(--spacing-lg);
}
.info-bar > div { display: flex; flex-direction: column; gap: 2px; }
.info-label { font-size: 10px; color: var(--color-text-muted); text-transform: uppercase; letter-spacing: 0.5px; }
.info-bar strong { font-size: var(--text-md); }
.info-divider { border-left: 1px solid var(--color-bg-separator); padding-left: var(--spacing-base); }
.valor strong { color: var(--color-accent); }
@media (max-width: 480px) {
  .info-bar { grid-template-columns: 1fr; }
  .info-divider { border-left: none; padding-left: 0; border-top: 1px solid var(--color-bg-separator); padding-top: var(--spacing-sm); }
  .checkout-card { padding: var(--spacing-base); }
}
.pix-section { display: flex; flex-direction: column; gap: var(--spacing-base); align-items: center; }
.qr-wrap {
  padding: var(--spacing-base);
  background: #fff;
  border: 2px solid var(--color-bg-separator);
  border-radius: var(--radius-md);
}
.qr-img { display: block; width: 200px; height: 200px; }
.qr-placeholder {
  width: 200px; height: 200px;
  display: flex; align-items: center; justify-content: center;
  color: var(--color-text-muted); font-size: var(--text-sm);
}
.copia-cola-wrap { width: 100%; }
.copia-help { font-size: var(--text-sm); color: var(--color-text-secondary); margin-bottom: var(--spacing-sm); text-align: center; }
.copia-row {
  display: flex; gap: var(--spacing-sm);
  background: var(--color-bg-hover);
  border-radius: var(--radius-md);
  padding: var(--spacing-sm);
  align-items: center;
}
.copia-code {
  flex: 1; font-size: 11px; color: var(--color-text-secondary);
  word-break: break-all; max-height: 60px; overflow-y: auto;
}
.btn-copy {
  background: var(--color-accent); color: #fff;
  border: none; padding: 8px 14px; border-radius: var(--radius-sm);
  font-size: var(--text-xs); font-weight: 600; cursor: pointer;
  flex-shrink: 0;
}
.btn-copy:hover { background: var(--color-accent-dark); }
.expira { font-size: var(--text-xs); color: var(--color-text-muted); text-align: center; margin-top: var(--spacing-sm); }
.poll-bar {
  display: flex; align-items: center; gap: var(--spacing-sm);
  justify-content: center;
  margin-top: var(--spacing-lg);
  padding: var(--spacing-sm);
  background: var(--color-bg-hover);
  border-radius: var(--radius-md);
  font-size: var(--text-sm); color: var(--color-text-secondary);
}
.dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: var(--color-bg-separator);
}
.dot.active {
  background: var(--color-success);
  animation: pulse 1.5s infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
.footer-instructions {
  margin-top: var(--spacing-lg);
  padding: var(--spacing-base);
  background: #f0f9ff;
  border-radius: var(--radius-md);
  font-size: var(--text-sm); color: var(--color-text-secondary);
  text-align: center;
}
.pix-erro { padding: var(--spacing-base); text-align: center; color: var(--color-text-muted); }
.pix-aguardando { padding: var(--spacing-base); text-align: center; color: var(--color-text-muted); }
.pix-aguardando .vence { margin-top: var(--spacing-xs); font-size: 0.9rem; }
.loader {
  width: 28px; height: 28px;
  border: 3px solid var(--color-bg-separator);
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
